import type { SubscriptionTypes } from "@component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import type {
	FactoringLeadCalculatorRequest,
	FactoringLeadCalculatorResponse,
	FactoringLeadFormFields,
	FactoringLeadFormProps,
	FactoringLeadFormState,
} from "./types";

/**
 * @Shortcode
 *
 * @title "Faktoring leadform"
 * @shortcode "factoring_lead"
 * @slug "faktoring"
 * @type "leadform"
 */
export class FactoringLeadForm<
	TLeadCalculatorRequest extends FactoringLeadCalculatorRequest,
	TLeadCalculatorResponse extends FactoringLeadCalculatorResponse,
	TFormFields extends FactoringLeadFormFields,
	TProps extends FactoringLeadFormProps<TLeadCalculatorRequest, TLeadCalculatorResponse>,
	TState extends FactoringLeadFormState<TFormFields>,
> extends AbstractLeadForm<TLeadCalculatorRequest, TLeadCalculatorResponse, TFormFields, TProps, TState> {
	// EventParams
	protected readonly productTypes: string = "FACTORING";
	protected readonly componentType: string = "leadform";
	protected readonly subscriptionTypes: SubscriptionTypes[] = ["CORPORATE_NEWSLETTER"];

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		colorScheme: "light",
		bottomLogo: false,
	};

	protected renderSteps(): ReactNode {
		const { title, variant } = this.props;

		const {
			values: { email },
			submitState,
		} = this.state;

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					title={title}
					withExtraInputs={variant !== "small-calculator"}
					submitState={submitState}
					canStepBack
				/>
				<Step.SuccessPage
					stepName="success-page"
					userEmail={email}
					subscriptionTypes={this.subscriptionTypes}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	@autobind
	protected onSuccessPage(): void {
		this.sendLead()
			.then((_response) => {
				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getAutoresponderName(): string {
		return "autoresponder-factoring";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const { lastName, firstName } = this.state.values;
		const {
			response: { factoringAmount },
		} = this.getLeadData();

		return {
			lastName,
			firstName,
			factoringAmount: factoringAmount ? formatText(factoringAmount, "ft") : undefined,
		};
	}

	protected getLeadDataRequest(): TLeadCalculatorRequest {
		const {
			values: { companyName, grossIncome },
		} = this.state;

		return {
			...this.getCalculatorRequest(),
			companyName,
			grossIncome,
		};
	}

	protected getLeadDataResponse(): TLeadCalculatorResponse {
		const {
			values: { factoringAmount },
		} = this.state;

		return {
			factoringAmount,
			...super.getCalculatorResponse(),
		};
	}

	protected getCalculationData(): string {
		return undefined;
	}

	protected getProductType(): ProductType {
		return "FACTORING";
	}

	protected getComponentName(): string {
		return "FactoringLeadForm";
	}
}

export default FactoringLeadForm;
